import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, CircularProgress, keyframes } from "@mui/material";
import io from "socket.io-client";
import { bindGameResync } from "../../../utils/bindGameResync";
import { describeBuzzerRound } from "../../../utils/buzzerRoundState";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const pulseAnim = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const STATE_CONFIG = {
  locked: {
    bg: "linear-gradient(135deg, #424242, #616161)",
    border: "#9e9e9e",
    label: "LOCKED",
    sublabel: "Waiting for round to start...",
  },
  active: {
    bg: "linear-gradient(135deg, #00c853, #64dd17)",
    border: "#00e676",
    label: "BUZZ NOW!",
    sublabel: "Who will be first?",
  },
  buzzed: {
    bg: "linear-gradient(135deg, #ffb300, #ff6f00)",
    border: "#ffca28",
    label: "BUZZED!",
    sublabel: "",
  },
  timed_out: {
    bg: "linear-gradient(135deg, #d50000, #ff1744)",
    border: "#ff5252",
    label: "TIME'S UP",
    sublabel: "No one buzzed in time",
  },
};

const SpectatorBuzzer = () => {
  const [state, setState] = useState("locked");
  const [winnerName, setWinnerName] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let countdownTimer = null;
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    const clearCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    };

    const applyDescribed = (described) => {
      setState(described.state);
      setWinnerName(described.winnerName || "");
      setCountdown(described.state === "active" ? described.countdown : null);
      if (described.state === "active" && described.countdown > 0) {
        clearCountdown();
        let remaining = described.countdown;
        countdownTimer = setInterval(() => {
          remaining -= 1;
          if (remaining <= 0) {
            clearCountdown();
            setCountdown(0);
          } else {
            setCountdown(remaining);
          }
        }, 1000);
      } else {
        clearCountdown();
      }
    };

    const syncRound = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/buzzer/1/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.success) return;
        applyDescribed(describeBuzzerRound(data.config, data.lastRound));
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    syncRound();
    socket.on("connect", syncRound);
    const unbindResync = bindGameResync(syncRound);

    socket.on("buzzer.activated", (data) => {
      setState("active");
      setWinnerName("");
      const seconds = data.countdown_seconds || 10;
      setCountdown(seconds);
      let remaining = seconds;
      clearCountdown();
      countdownTimer = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          clearCountdown();
          setCountdown(0);
        } else {
          setCountdown(remaining);
        }
      }, 1000);
    });

    socket.on("buzzer.attempt", (data) => {
      if (data.is_winner) {
        setState("buzzed");
        setWinnerName(data.display_name || "Someone");
        setCountdown(null);
        clearCountdown();
      }
    });

    socket.on("buzzer.timeout", () => {
      setState("timed_out");
      setCountdown(null);
      clearCountdown();
    });

    socket.on("buzzer.locked", () => {
      setState("locked");
      setWinnerName("");
      setCountdown(null);
      clearCountdown();
    });

    return () => {
      cancelled = true;
      unbindResync();
      clearCountdown();
      socket.disconnect();
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
        <CircularProgress sx={{ color: "#00c853" }} />
      </Box>
    );
  }

  const cfg = STATE_CONFIG[state] || STATE_CONFIG.locked;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "#0a0a0a",
        px: 3,
      }}
    >
      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.4rem", md: "2rem" }, mb: 4 }}>
        Beat the Buzzer
      </Typography>

      {/* Big buzzer circle display */}
      <Box
        sx={{
          width: { xs: 220, md: 300 },
          height: { xs: 220, md: 300 },
          borderRadius: "50%",
          background: cfg.bg,
          border: `6px solid ${cfg.border}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 0 40px ${cfg.border}44`,
          animation: state === "active" ? `${pulseAnim} 1.5s ease-in-out infinite` : "none",
        }}
      >
        {countdown !== null && (
          <Typography sx={{ color: "#fff", fontSize: "3rem", fontWeight: 800 }}>
            {countdown}
          </Typography>
        )}
        <Typography sx={{ color: "#fff", fontSize: { xs: "1.2rem", md: "1.6rem" }, fontWeight: 700, textAlign: "center" }}>
          {cfg.label}
        </Typography>
      </Box>

      {/* Winner */}
      {state === "buzzed" && winnerName && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#FFD700", fontSize: "1.5rem", fontWeight: 800 }}>
            {winnerName}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>
            buzzed first!
          </Typography>
        </Box>
      )}

      {/* Sublabel */}
      {cfg.sublabel && state !== "buzzed" && (
        <Typography sx={{ color: "rgba(255,255,255,0.5)", mt: 3, fontSize: "1rem" }}>
          {cfg.sublabel}
        </Typography>
      )}
    </Box>
  );
};

export default SpectatorBuzzer;
